/**
 * Library Tab Component
 * Displays all generated AI content with filtering and actions
 */
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FolderOpen,
  Loader2,
  Sparkles,
  Image as ImageIcon,
  Video,
  Bot,
  Megaphone,
  Play,
  Check,
  X,
  Download,
  CalendarClock,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Star,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

export const LibraryTab = ({
  library,
  libraryLoading,
  libraryFilter,
  setLibraryFilter,
  libraryPage,
  setLibraryPage,
  libraryTotal,
  libraryTotalPages,
  onDownload,
  onSchedule,
  onDelete,
  onNavigateToGenerate,
  token,
  API,
  onRefresh,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600">
              <FolderOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">AI Library</h2>
              <p className="text-gray-500 text-sm">All your generated content in one place</p>
            </div>
          </div>
          
          {/* Filter Dropdown */}
          <div className="flex items-center gap-3">
            <Label className="text-gray-600 text-sm">Filter:</Label>
            <Select value={libraryFilter} onValueChange={(v) => { setLibraryFilter(v); setLibraryPage(1); }}>
              <SelectTrigger className="w-32 bg-gray-50 dark:bg-gray-700 border-gray-200" data-testid="library-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
            <Star className="h-4 w-4 fill-amber-400" />
            <span><strong>Tip:</strong> Star items to save them permanently. Unstarred items are automatically deleted after 7 days.</span>
          </div>
        </div>

        {/* Content */}
        {libraryLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-600">Loading library...</span>
          </div>
        ) : library.length === 0 ? (
          <EmptyState onNavigateToGenerate={onNavigateToGenerate} />
        ) : (
          <>
            <LibraryTable 
              library={library}
              onDownload={onDownload}
              onSchedule={onSchedule}
              onDelete={onDelete}
              token={token}
              API={API}
              onRefresh={onRefresh}
            />
            
            {/* Pagination */}
            {libraryTotalPages > 1 && (
              <Pagination
                libraryPage={libraryPage}
                setLibraryPage={setLibraryPage}
                libraryTotal={libraryTotal}
                libraryTotalPages={libraryTotalPages}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ onNavigateToGenerate }) => (
  <div className="text-center py-12">
    <FolderOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No generations yet</h3>
    <p className="text-gray-500 mb-4">Start creating images and videos to build your library</p>
    <Button 
      onClick={onNavigateToGenerate}
      className="bg-gradient-to-r from-emerald-600 to-teal-600"
    >
      <Sparkles className="h-4 w-4 mr-2" />
      Generate Content
    </Button>
  </div>
);

const LibraryTable = ({ library, onDownload, onSchedule, onDelete, token, API, onRefresh }) => (
  <div className="overflow-x-auto">
    <table className="w-full" data-testid="library-table">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Date</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Type</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Preview</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Prompt</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
          <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
        </tr>
      </thead>
      <tbody>
        {library.map((item) => (
          <LibraryRow 
            key={item.generation_id}
            item={item}
            onDownload={onDownload}
            onSchedule={onSchedule}
            onDelete={onDelete}
            token={token}
            API={API}
            onRefresh={onRefresh}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const LibraryRow = ({ item, onDownload, onSchedule, onDelete, token, API, onRefresh }) => {
  const [starring, setStarring] = useState(false);
  const [starred, setStarred] = useState(item.starred || false);
  
  const toggleStar = async () => {
    setStarring(true);
    try {
      const res = await fetch(`${API}/ai/library/${item.generation_id}/star`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setStarred(data.starred);
        toast.success(data.message);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error("Failed to update star status");
    } finally {
      setStarring(false);
    }
  };
  
  return (
    <tr className="border-b border-slate-100 hover:bg-gray-50 dark:hover:bg-gray-700" data-testid={`library-row-${item.generation_id}`}>
      {/* Date */}
      <td className="py-3 px-4">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {new Date(item.created_at).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric"
          })}
        </div>
        <div className="text-xs text-gray-400">
          {new Date(item.created_at).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit"
          })}
        </div>
        {/* Deletion warning for non-starred items */}
        {!starred && item.days_until_deletion !== null && item.days_until_deletion !== undefined && (
          <div className="flex items-center gap-1 mt-1">
            <Clock className="h-3 w-3 text-amber-500" />
            <span className="text-xs text-amber-600 dark:text-amber-400">
              {item.days_until_deletion > 0 
                ? `Deletes in ${item.days_until_deletion}d` 
                : "Deleting soon"}
            </span>
          </div>
        )}
      </td>
      
      {/* Type Badge */}
      <td className="py-3 px-4">
        <TypeBadge type={item.type} />
      </td>
      
      {/* Preview */}
      <td className="py-3 px-4">
        <PreviewCell item={item} token={token} API={API} />
      </td>
      
      {/* Prompt */}
      <td className="py-3 px-4 max-w-xs">
        <p className="text-sm text-gray-700 dark:text-gray-300 truncate" title={item.prompt || item.website_url || item.campaign_name}>
          {item.prompt || item.website_url || item.campaign_name || "-"}
        </p>
        <p className="text-xs text-gray-400">{item.credits_used} credits</p>
      </td>
      
      {/* Status */}
      <td className="py-3 px-4">
        <StatusBadge status={item.status} />
      </td>
      
      {/* Actions */}
      <td className="py-3 px-4">
        <div className="flex items-center justify-end gap-2">
          {/* Star Button */}
          <Button
            size="sm"
            variant={starred ? "default" : "outline"}
            onClick={toggleStar}
            disabled={starring}
            className={starred 
              ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500" 
              : "text-amber-500 hover:text-amber-600 hover:bg-amber-50 border-amber-200"
            }
            title={starred ? "Saved permanently" : "Star to save permanently"}
            data-testid={`star-${item.generation_id}`}
          >
            {starring ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Star className={`h-3.5 w-3.5 ${starred ? "fill-white" : ""}`} />
            )}
          </Button>
          
          {(item.image_base64 || item.video_base64 || item.video_file || item.has_video) && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(item)}
                className="text-xs"
                data-testid={`download-${item.generation_id}`}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSchedule(item)}
                className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                data-testid={`schedule-${item.generation_id}`}
              >
                <CalendarClock className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(item.generation_id)}
            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            data-testid={`delete-${item.generation_id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

const TypeBadge = ({ type }) => {
  const config = {
    image: { bg: "bg-purple-100", text: "text-purple-700", icon: ImageIcon },
    video: { bg: "bg-pink-100", text: "text-pink-700", icon: Video },
    automation: { bg: "bg-blue-100", text: "text-blue-700", icon: Bot },
    campaign: { bg: "bg-blue-100", text: "text-blue-700", icon: Megaphone },
  };
  
  const { bg, text, icon: Icon } = config[type] || config.image;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="h-3 w-3" />
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        Processing
      </span>
    );
  }
  
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <X className="h-3 w-3" />
        Failed
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <Check className="h-3 w-3" />
      Complete
    </span>
  );
};

const PreviewCell = ({ item, token, API }) => {
  if (item.type === "image" && item.image_base64) {
    return (
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
        <img 
          src={`data:image/png;base64,${item.image_base64}`} 
          alt="Preview"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  
  if (item.type === "image" && item.status === "completed") {
    return (
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
        <img 
          src={`${API}/ai/stream/${item.generation_id}?token=${token}`} 
          alt="Preview"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  
  if (item.type === "video" && (item.status === "completed" || item.has_video)) {
    return (
      <div className="w-20 h-14 rounded-lg overflow-hidden bg-slate-900 relative group">
        <video 
          src={`${API}/ai/stream/${item.generation_id}?token=${token}`}
          className="w-full h-full object-cover"
          controls
          muted
          preload="metadata"
          playsInline
          onMouseEnter={(e) => e.target.play().catch(() => {})}
          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
          data-testid={`video-preview-${item.generation_id}`}
        />
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
          <Play className="h-5 w-5 text-white" />
        </div>
      </div>
    );
  }
  
  if (item.type === "video" && item.status === "pending") {
    return (
      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }
  
  // Default placeholder
  const Icon = item.type === "automation" ? Bot : item.type === "campaign" ? Megaphone : ImageIcon;
  return (
    <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
      <Icon className="h-6 w-6 text-gray-400" />
    </div>
  );
};

const Pagination = ({ libraryPage, setLibraryPage, libraryTotal, libraryTotalPages }) => (
  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
    <p className="text-sm text-gray-500">
      Showing {(libraryPage - 1) * 10 + 1} to {Math.min(libraryPage * 10, libraryTotal)} of {libraryTotal} items
    </p>
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setLibraryPage(p => Math.max(1, p - 1))}
        disabled={libraryPage <= 1}
        data-testid="library-prev"
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        Previous
      </Button>
      <span className="text-sm text-gray-600 px-3">
        Page {libraryPage} of {libraryTotalPages}
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setLibraryPage(p => Math.min(libraryTotalPages, p + 1))}
        disabled={libraryPage >= libraryTotalPages}
        data-testid="library-next"
      >
        Next
        <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  </div>
);

export default LibraryTab;
